import type { IFormEventContext } from '@/Core/Contracts/UI/Form/IFormConfig'

interface FileLike {
    uuid?: string
    createdAt?: string
}

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : value != null ? [value] : [])

/**
 * Двокрокове прикріплення файлів (`SPEC-008` §5.7.1, G-05): щойно завантажені
 * файли поля `field` (або кількох полів) — у `fileUuids` тіла запиту запису,
 * а самі поля з тіла прибираються.
 *
 * Крок 1 — ядровий `FileInput` вантажить файл на `uploadUrl` поля, і бек кладе
 * його сиротою. Крок 2 — `POST` / `PATCH` запису з `fileUuids`: бек бере лише
 * сиріт поточного користувача з цими uuid.
 *
 * Прикріплюємо **лише нові** завантаження. Повторно надісланий uuid уже
 * прикріпленого файлу дав би 422: серед сиріт бек його не знайде.
 *
 * Ознака нового файлу — `createdAt`. Це не здогадка, а контракт серіалізації:
 * `createdAt` є лише в групі `file:read`, якою відповідає контролер
 * завантаження; запис віддає свої файли групою `file:short:read`, де цього
 * поля немає. Тож `createdAt` є ⇔ файл прийшов із відповіді на завантаження.
 *
 * ⚠️ Інваріант тримається на тому, що контролери **запису** не віддають групу
 * `file:read` (`.claude/rules/development.md`). Повернете її — розрізнювач
 * зламається, і збереження картки почне падати на 422.
 *
 * Поле прибираємо, бо інакше save-цикл ядра згорнув би файл в IRI, а бек
 * приймає файли лише через `fileUuids`. Зразок — `attachUploadedFiles`
 * замовлень PD (там поле завжди `files`).
 *
 * Підключення — у `form.eventHandlers.onBeforeTransformSendSaveRequest`
 * ресурсу: `{ contractFileUuids: attachUploadedFiles('file') }`.
 *
 * Полів може бути кілька (Р10-4, `SPEC-010` §9.4): у перевізника чотири
 * файлові поля за категоріями, а `fileUuids` у тілі — один спільний перелік.
 * Категорію файл отримав ще на кроці 1 (адреса завантаження поля), тож
 * прикріплення її не передає. Перелік полів — масивом:
 * `attachUploadedFiles(['filesRegistrationDocuments', 'filesOther'])`.
 */
export const attachUploadedFiles =
    (field: string | readonly string[] = 'files') =>
    ({ values, formContext }: IFormEventContext): void => {
        const fields: readonly string[] = typeof field === 'string' ? [field] : field
        const byUuid = new Map<string, FileLike>()
        const collected = fields.flatMap((name) => [
            ...toArray(formContext?.getInitialValue?.(name)),
            ...toArray(values[name]),
        ])

        for (const raw of collected) {
            const file: FileLike = typeof raw === 'object' && raw !== null ? (raw as FileLike) : {}
            const uuid = file.uuid ?? (typeof raw === 'string' ? raw : undefined)

            if (typeof uuid !== 'string' || uuid === '') continue

            // Той самий файл може прийти і з початкових значень, і з поточних —
            // ознаку нового не губимо
            const known = byUuid.get(uuid)

            byUuid.set(uuid, known?.createdAt ? known : file)
        }

        values['fileUuids'] = Array.from(byUuid.entries())
            .filter(([, file]) => Boolean(file.createdAt))
            .map(([uuid]) => uuid)

        for (const name of fields) delete values[name]
    }
